import { useOnboarding, PRICING } from "./OnboardingContext";
import { CreditCard, Lock, ArrowLeft, Loader2, ExternalLink, CheckCircle, AlertCircle, Clock, Info } from "lucide-react";
import { useState, useEffect, useCallback } from "react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StepPayment = () => {
  const {
    monthlyPrice,
    setupFee,
    selectedPackage,
    selectedDistributionTier,
    numberOfMachines,
    selectedAddOns,
    billingInfo,
    updateBillingInfo,
    setPaymentStatus,
    nextStep,
    prevStep,
    getPackageDetails
  } = useOnboarding();

  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  const pkg = getPackageDetails();
  const hasAcceleratedDeployment = selectedAddOns.includes("acceleratedDeployment");

  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setCheckingPayment(false);
      setError("Impossible de vérifier le statut du paiement. Veuillez nous contacter.");
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/stripe/checkout-status/${sessionId}`);
      if (!response.ok) throw new Error('Failed to check payment status');
      
      const data = await response.json();
      
      if (data.payment_status === 'paid') {
        setCheckingPayment(false);
        setPaymentSuccess(true);
        setPaymentStatus("completed");
        setTimeout(() => nextStep(), 2000);
        return;
      } else if (data.status === 'expired') {
        setCheckingPayment(false);
        setError("La session de paiement a expiré. Veuillez réessayer.");
        return;
      }

      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (err) {
      setCheckingPayment(false);
      setError("Erreur lors de la vérification du paiement.");
    }
  }, [setPaymentStatus, nextStep]);
  
  // Check for Stripe return URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentParam = urlParams.get('payment');
    const sessionId = urlParams.get('session_id');
    
    if (paymentParam === 'success' && sessionId) {
      setCheckingPayment(true);
      pollPaymentStatus(sessionId);
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (paymentParam === 'cancelled') {
      setError("Le paiement a été annulé. Vous pouvez réessayer.");
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [pollPaymentStatus]);

  const handleStripePayment = async (e) => {
    e.preventDefault();
    setIsProcessing(true);
    setError(null);
    
    try {
      const originUrl = window.location.origin;
      
      const response = await fetch(`${API_URL}/api/stripe/create-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          package_id: selectedPackage,
          distribution_tier: selectedDistributionTier || "essentielle",
          additional_machines: Math.max(0, numberOfMachines - 1),
          additional_buses: 0,
          add_ons: selectedAddOns,
          origin_url: originUrl,
          customer_email: billingInfo.email,
          company_name: billingInfo.companyName,
          envelope_id: localStorage.getItem('docusign_envelope_id') || null,
          is_preorder: true
        })
      });
      
      const data = await response.json();
      
      if (data.success && data.checkout_url) {
        localStorage.setItem('stripe_session_id', data.session_id);
        window.location.href = data.checkout_url;
      } else {
        setError(data.error || "Erreur lors de la création de la session de paiement.");
        setIsProcessing(false);
      }
      
    } catch (err) {
      setError("Erreur de connexion. Veuillez vérifier votre connexion internet.");
      setIsProcessing(false);
    }
  };

  if (!pkg) return null;

  // Show payment verification screen
  if (checkingPayment) {
    return (
      <div data-testid="step-payment" className="animate-fade-in">
        <div className="text-center py-16">
          <Loader2 size={48} className="animate-spin text-[#B89F7C] mx-auto mb-6" />
          <h2 className="font-heading text-2xl text-[#0A0A0A] mb-4">
            Vérification du paiement...
          </h2>
          <p className="font-body text-[#525252]">
            Veuillez patienter pendant que nous confirmons votre paiement.
          </p>
        </div>
      </div>
    );
  }

  // Show success screen
  if (paymentSuccess) {
    return (
      <div data-testid="step-payment" className="animate-fade-in">
        <div className="text-center py-16">
          <div className="w-20 h-20 mx-auto mb-6 bg-green-50 flex items-center justify-center rounded-full">
            <CheckCircle size={40} className="text-green-600" />
          </div>
          <h2 className="font-heading text-2xl text-[#0A0A0A] mb-4">
            Précommande validée !
          </h2>
          <p className="font-body text-[#525252] mb-4">
            Votre paiement de {setupFee}€ HT a été confirmé.
          </p>
          <p className="font-body text-sm text-[#B89F7C]">
            Redirection vers la confirmation...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="step-payment" className="animate-fade-in">
      {/* Header */}
      <div className="mb-8">
        <p className="font-body text-sm tracking-[0.15em] uppercase text-[#B89F7C] mb-2">
          Étape 7
        </p>
        <h1 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-[#0A0A0A] mb-4">
          Validation de la précommande
        </h1>
        <p className="font-body text-[#525252]">
          Finalisez votre réservation avec un paiement sécurisé via Stripe.
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 mb-6 flex items-start gap-3">
          <AlertCircle size={20} className="shrink-0 mt-0.5" />
          <div>
            <p className="font-body text-sm">{error}</p>
            <button 
              onClick={() => setError(null)}
              className="text-sm underline mt-1 hover:no-underline"
            >
              Fermer
            </button>
          </div>
        </div>
      )}

      {/* Payment Explanation Banner */}
      <div className="bg-[#F9F6F0] border border-[#E5E5E5] p-6 mb-8">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 bg-white flex items-center justify-center shrink-0">
            <Info size={20} className="text-[#B89F7C]" />
          </div>
          <div>
            <h3 className="font-heading text-lg text-[#0A0A0A] mb-2">Ce que couvre ce paiement</h3>
            <ul className="space-y-2">
              <li className="font-body text-sm text-[#525252] flex items-start gap-2">
                <span className="text-[#B89F7C] mt-1">•</span>
                <span><strong>Frais de réservation</strong> — Garantit votre place dans notre planning de déploiement</span>
              </li>
              <li className="font-body text-sm text-[#525252] flex items-start gap-2">
                <span className="text-[#B89F7C] mt-1">•</span>
                <span><strong>Préparation du projet</strong> — Configuration initiale et planification</span>
              </li>
              <li className="font-body text-sm text-[#525252] flex items-start gap-2">
                <span className="text-[#B89F7C] mt-1">•</span>
                <span><strong>Mise en service</strong> — Installation et activation du dispositif</span>
              </li>
            </ul>
            <div className="mt-4 pt-4 border-t border-[#E5E5E5]">
              <p className="font-body text-xs text-[#525252] flex items-center gap-2">
                <Clock size={14} className="text-[#B89F7C]" />
                La facturation récurrente ({monthlyPrice}€ HT/mois) débutera à compter de la mise en service effective.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-5 gap-8">
        {/* Payment Form */}
        <div className="lg:col-span-3">
          <form onSubmit={handleStripePayment} className="space-y-6">
            {/* Billing Info Summary */}
            <div className="bg-white border border-[#E5E5E5] p-6">
              <h3 className="font-heading text-lg text-[#0A0A0A] mb-4">
                Informations de facturation
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="font-body text-sm text-[#525252]">Entreprise</span>
                  <span className="font-body text-sm text-[#0A0A0A] font-medium">{billingInfo.companyName || "—"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-body text-sm text-[#525252]">Contact</span>
                  <span className="font-body text-sm text-[#0A0A0A]">{billingInfo.contactName || "—"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-body text-sm text-[#525252]">Email</span>
                  <span className="font-body text-sm text-[#0A0A0A]">{billingInfo.email || "—"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-body text-sm text-[#525252]">SIRET</span>
                  <span className="font-body text-sm text-[#0A0A0A]">{billingInfo.siret || "—"}</span>
                </div>
              </div>
            </div>

            {/* Stripe Payment Info */}
            <div className="bg-[#0A0A0A] text-white p-6">
              <div className="flex items-center gap-3 mb-4">
                <CreditCard size={24} className="text-[#B89F7C]" />
                <h3 className="font-heading text-lg">
                  Paiement sécurisé Stripe
                </h3>
              </div>
              <p className="font-body text-sm text-white/70 mb-4">
                Vous serez redirigé vers la page de paiement sécurisée Stripe pour valider votre précommande.
              </p>
              <div className="flex items-center gap-2 text-white/50">
                <Lock size={14} />
                <span className="font-body text-xs">
                  Paiement 100% sécurisé • Certifié PCI DSS
                </span>
              </div>
            </div>

            {/* Submit */}
            <div className="flex justify-between pt-6 border-t border-[#E5E5E5]">
              <button
                type="button"
                onClick={prevStep}
                data-testid="btn-prev-step"
                className="flex items-center gap-2 font-body text-sm text-[#525252] hover:text-[#0A0A0A] transition-colors"
              >
                <ArrowLeft size={16} />
                Retour
              </button>
              <button
                type="submit"
                disabled={isProcessing}
                data-testid="btn-pay"
                className="bg-[#0A0A0A] text-white hover:bg-[#B89F7C] transition-colors duration-300 px-10 py-4 text-sm tracking-widest uppercase font-medium inline-flex items-center gap-3 disabled:opacity-50"
              >
                {isProcessing ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Redirection vers Stripe...
                  </>
                ) : (
                  <>
                    <Lock size={16} />
                    Valider ma précommande • {setupFee}€ HT
                    <ExternalLink size={14} />
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Order Summary */}
        <div className="lg:col-span-2">
          <div className="bg-[#0A0A0A] text-white p-6 sticky top-24">
            <div className="flex items-center gap-2 mb-4">
              <Clock size={16} className="text-[#B89F7C]" />
              <span className="bg-[#B89F7C] text-[#0A0A0A] px-2 py-0.5 text-[10px] font-medium tracking-wider uppercase">
                Précommande
              </span>
            </div>
            <h3 className="font-body text-sm tracking-[0.15em] uppercase text-[#B89F7C] mb-4">
              À régler aujourd'hui
            </h3>
            <div className="space-y-3 mb-6 pb-6 border-b border-white/20">
              <div className="flex justify-between font-body text-sm">
                <span className="text-white/70">Frais de réservation et mise en service</span>
                <span>{setupFee}€ HT</span>
              </div>
            </div>
            <div className="flex justify-between items-end mb-6">
              <span className="font-body text-white/70">Total</span>
              <span className="font-heading text-3xl">{setupFee}€ <span className="text-base">HT</span></span>
            </div>
            
            <div className="pt-4 border-t border-white/20 mb-4">
              <p className="font-body text-xs text-white/50 mb-2">
                Puis, à compter de la mise en service :
              </p>
              <p className="font-heading text-lg">{monthlyPrice}€ <span className="text-sm font-body text-white/70">HT/mois</span></p>
            </div>

            <p className="font-body text-xs text-white/50">
              Engagement : {pkg.engagement} mois
            </p>

            {hasAcceleratedDeployment && (
              <div className="mt-4 pt-4 border-t border-white/10">
                <p className="font-body text-xs text-[#B89F7C] flex items-center gap-2">
                  <CheckCircle size={12} />
                  Déploiement accéléré inclus
                </p>
              </div>
            )}
            
            {/* Payment methods */}
            <div className="mt-6 pt-4 border-t border-white/10">
              <p className="font-body text-xs text-white/50 mb-2">Moyens de paiement acceptés :</p>
              <div className="flex gap-2">
                <div className="bg-white/10 px-2 py-1 rounded text-xs">Visa</div>
                <div className="bg-white/10 px-2 py-1 rounded text-xs">Mastercard</div>
                <div className="bg-white/10 px-2 py-1 rounded text-xs">SEPA</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StepPayment;
